import React, { useState, useEffect, useRef } from "react";
import * as THREE from "three";
import { FontLoader } from "three/examples/jsm/loaders/FontLoader.js";
import {
  FONT_URL,
  SUBTITLE_FONT_URL,
  PARTICLE_IMAGE_URL,
  DEFAULT_IMAGE_URL,
} from "../constants/particleTextConstants";
import { TILE_POSITIONS } from "../constants/projectsConstants";
import { AssetProvider } from "../contexts/AssetContext";

const GlobalPreloader = ({ children }) => {
  const [progress, setProgress] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preloadedAssets, setPreloadedAssets] = useState({});
  const loadingManagerRef = useRef(null);

  useEffect(() => {
    const preloadAssets = async () => {
      try {
        setIsLoading(true);
        setProgress(0);
        setError(null);

        // Create a loading manager to track overall progress
        const loadingManager = new THREE.LoadingManager(
          () => {
            // All assets loaded
            console.log("All assets loaded successfully");
            setIsLoading(false);
            setProgress(100);
          },
          (url, loaded, total) => {
            // Progress update
            const progressValue = (loaded / total) * 100;
            console.log(
              `Loading progress: ${Math.round(progressValue)}% (${url})`,
            );
            setProgress(progressValue);
          },
          (url) => {
            // Error handler
            console.error(`Error loading asset: ${url}`);
            setError(`Failed to load asset: ${url}`);
          },
        );

        loadingManagerRef.current = loadingManager;

        // Create texture loader with the loading manager
        const textureLoader = new THREE.TextureLoader(loadingManager);
        const fontLoader = new FontLoader(loadingManager);

        // Collect all assets to preload
        const assets = {};

        // Load main font
        const fontPromise = new Promise((resolve, reject) => {
          fontLoader.load(
            FONT_URL,
            (font) => {
              console.log("Main font loaded successfully");
              assets.font = font;
              resolve();
            },
            undefined,
            (error) => {
              console.error("Error loading main font:", error);
              reject(error);
            },
          );
        });

        // Load subtitle font
        const subtitleFontPromise = new Promise((resolve, reject) => {
          fontLoader.load(
            SUBTITLE_FONT_URL,
            (font) => {
              console.log("Subtitle font loaded successfully");
              assets.subtitleFont = font;
              resolve();
            },
            undefined,
            (error) => {
              console.error("Error loading subtitle font:", error);
              reject(error);
            },
          );
        });

        // Load particle texture
        const particleTexturePromise = new Promise((resolve, reject) => {
          textureLoader.load(
            PARTICLE_IMAGE_URL,
            (texture) => {
              console.log("Particle texture loaded successfully");
              assets.particleTexture = texture;
              resolve();
            },
            undefined,
            (error) => {
              console.error("Error loading particle texture:", error);
              reject(error);
            },
          );
        });

        // Load default image texture
        const defaultImagePromise = new Promise((resolve, reject) => {
          textureLoader.load(
            DEFAULT_IMAGE_URL,
            (texture) => {
              console.log("Default image loaded successfully");
              texture.colorSpace = THREE.SRGBColorSpace;
              texture.minFilter = THREE.LinearMipmapLinearFilter;
              texture.magFilter = THREE.LinearFilter;
              texture.needsUpdate = true;
              assets.defaultImage = texture;
              resolve();
            },
            undefined,
            (error) => {
              console.error("Error loading default image:", error);
              reject(error);
            },
          );
        });

        // Load project images
        const projectImagePromises = [];
        const uniqueProjectImages = [
          ...new Set(TILE_POSITIONS.map((tile) => tile.image)),
        ];
        
        uniqueProjectImages.forEach((imageUrl, index) => {
          const projectImagePromise = new Promise((resolve, reject) => {
            textureLoader.load(
              imageUrl,
              (texture) => {
                console.log(`Project image ${index + 1} loaded: ${imageUrl}`);
                texture.colorSpace = THREE.SRGBColorSpace;
                texture.minFilter = THREE.LinearMipmapLinearFilter;
                texture.magFilter = THREE.LinearFilter;
                texture.needsUpdate = true;
                // Store with the image URL as key for easy lookup
                if (!assets.projectImages) {
                  assets.projectImages = {};
                }
                assets.projectImages[imageUrl] = texture;
                resolve();
              },
              undefined,
              (error) => {
                console.error(`Error loading project image ${imageUrl}:`, error);
                reject(error);
              },
            );
          });
          projectImagePromises.push(projectImagePromise);
        });

        // Load about images
        const about1Promise = new Promise((resolve, reject) => {
          textureLoader.load(
            "/about1.png",
            (texture) => {
              console.log("About image 1 loaded successfully");
              texture.colorSpace = THREE.SRGBColorSpace;
              texture.minFilter = THREE.LinearMipmapLinearFilter;
              texture.magFilter = THREE.LinearFilter;
              texture.needsUpdate = true;
              assets.about1 = texture;
              resolve();
            },
            undefined,
            (error) => {
              console.error("Error loading about1 image:", error);
              reject(error);
            },
          );
        });

        const about2Promise = new Promise((resolve, reject) => {
          textureLoader.load(
            "/about2.png",
            (texture) => {
              console.log("About image 2 loaded successfully");
              texture.colorSpace = THREE.SRGBColorSpace;
              texture.minFilter = THREE.LinearMipmapLinearFilter;
              texture.magFilter = THREE.LinearFilter;
              texture.needsUpdate = true;
              assets.about2 = texture;
              resolve();
            },
            undefined,
            (error) => {
              console.error("Error loading about2 image:", error);
              reject(error);
            },
          );
        });

        // Combine all promises
        const allPromises = [
          fontPromise,
          subtitleFontPromise,
          particleTexturePromise,
          defaultImagePromise,
          ...projectImagePromises,
          about1Promise,
          about2Promise,
        ];

        // Wait for all assets to load
        await Promise.all(allPromises);

        // Store the loaded assets
        setPreloadedAssets(assets);
        console.log("All assets preloaded and stored in context");
      } catch (err) {
        console.error("Preloading error:", err);
        setError(err.message || "Failed to load assets");
      }
    };

    preloadAssets();

    // Cleanup function
    return () => {
      // Cancel any pending loads if component unmounts
      if (loadingManagerRef.current) {
        loadingManagerRef.current.onLoad = () => {};
        loadingManagerRef.current.onProgress = () => {};
        loadingManagerRef.current.onError = () => {};
      }
    };
  }, []);

  if (error) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black z-50">
        <div className="text-center p-6 bg-red-900 rounded-lg">
          <h2 className="text-white text-xl mb-4">Preloading Error</h2>
          <p className="text-white mb-4">{error}</p>
          <button
            className="px-4 py-2 bg-white text-red-900 rounded hover:bg-gray-200"
            onClick={() => window.location.reload()}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black z-50">
        <div className="text-center">
          <p className="text-white text-2xl" style={{ fontFamily: "Azonix, sans-serif" }}>{progress.toFixed(0)}%</p>
        </div>
      </div>
    );
  }

  // Provide the preloaded assets to all child components
  return (
    <AssetProvider assets={preloadedAssets} isLoaded={!isLoading} error={error}>
      {children}
    </AssetProvider>
  );
};

export default GlobalPreloader;
